import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconComponent } from '../icon/icon.component';
import { IconName } from '../icon/icons';
import { uniqueId } from '../form/ids';

export interface ListNavItem {
  readonly label: string;
  readonly link: string;
  readonly icon?: IconName;
  /** Valore riassuntivo a destra, per esempio "Attiva" o "2 dispositivi". */
  readonly value?: string;
  /** Mancano dati necessari: compare il punto giallo con il testo nascosto "da completare". */
  readonly attention?: boolean;
  readonly testId?: string;
}

export interface ListNavGroup {
  readonly label?: string;
  readonly items: readonly ListNavItem[];
}

/**
 * Elenco a livelli per il telefono, come le impostazioni di iOS (tavole 5a e 5b): gruppi in
 * schede `rounded-2xl` con divisori, righe di 56 px con icona, testo a 16 px, valore e freccia.
 * Ogni riga porta alla pagina di dettaglio.
 */
@Component({
  selector: 'bs-list-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, IconComponent],
  host: { class: 'block' },
  template: `
    <nav [attr.aria-label]="label()" [attr.data-testid]="testId()" class="flex flex-col gap-6">
      @for (group of groups(); track $index; let index = $index) {
        <div class="flex flex-col gap-2">
          @if (group.label; as heading) {
            <h2
              [id]="groupId(index)"
              class="px-4 text-sm font-medium text-stone-600 dark:text-stone-300"
            >
              {{ heading }}
            </h2>
          }
          <ul
            class="divide-y divide-stone-200 overflow-hidden rounded-2xl border border-stone-200 bg-white dark:divide-stone-800 dark:border-stone-800 dark:bg-stone-900"
            [attr.aria-labelledby]="group.label ? groupId(index) : null"
          >
            @for (item of group.items; track item.link) {
              <li>
                <a
                  [routerLink]="item.link"
                  class="flex h-14 items-center gap-3 px-4 text-base text-ink transition-colors hover:bg-stone-50 dark:text-stone-50 dark:hover:bg-stone-800"
                  [attr.data-testid]="item.testId ?? null"
                >
                  @if (item.icon; as name) {
                    <span
                      class="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-200"
                    >
                      <bs-icon [name]="name" [size]="20" />
                    </span>
                  }
                  <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
                  @if (item.attention) {
                    <span
                      class="size-2 shrink-0 rounded-full bg-bsite-400"
                      aria-hidden="true"
                    ></span>
                    <span class="sr-only">, da completare</span>
                  }
                  @if (item.value; as value) {
                    <span
                      class="max-w-40 shrink-0 truncate text-sm text-stone-600 dark:text-stone-300"
                    >
                      <span class="sr-only">: </span>{{ value }}
                    </span>
                  }
                  <bs-icon
                    name="chevron-right"
                    [size]="18"
                    class="text-stone-400 dark:text-stone-500"
                  />
                </a>
              </li>
            }
          </ul>
        </div>
      }
    </nav>
  `,
})
export class ListNavComponent {
  readonly groups = input.required<readonly ListNavGroup[]>();
  /** Nome accessibile dell'elenco. */
  readonly label = input('Sezioni delle impostazioni');
  readonly testId = input<string | null>(null);

  private readonly id = uniqueId('bs-list-nav');

  protected groupId(index: number): string {
    return `${this.id}-group-${index}`;
  }
}
